import { useEffect, useRef, useState } from 'react';
import { isRecord } from './api';
import type { RealtimeEvent } from './types';

export type RealtimeHandler = (event: RealtimeEvent) => void;

export interface RealtimeState {
  /** Verbindung steht (inkl. erfolgreichem `hello` des Servers). */
  connected: boolean;
  /** Letzter Verbindungsfehler/Close-Code, für die Diagnose in der UI. */
  lastError: string | null;
}

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;
const PING_INTERVAL_MS = 25_000;

function parseEvent(raw: unknown): RealtimeEvent | null {
  if (typeof raw !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const type = parsed['type'];
  if (typeof type !== 'string') return null;
  const at = parsed['at'];
  return {
    type,
    payload: parsed['payload'],
    at: typeof at === 'number' ? at : Date.now(),
  };
}

function socketUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  return `${scheme}${window.location.host}/api/ws`;
}

/**
 * WebSocket-Verbindung zum Server mit Auto-Reconnect und exponentiellem Backoff.
 *
 * `onEvent` darf sich bei jedem Render ändern — der Handler wird in einem Ref
 * gehalten, damit die Verbindung davon unberührt bleibt.
 */
export function useRealtime(active: boolean, onEvent: RealtimeHandler): RealtimeState {
  const [connected, setConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const handlerRef = useRef(onEvent);
  useEffect(() => {
    handlerRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!active) {
      setConnected(false);
      return;
    }

    let disposed = false;
    let socket: WebSocket | null = null;
    let attempt = 0;
    let retryTimer: number | null = null;
    let pingTimer: number | null = null;

    const clearTimers = (): void => {
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (pingTimer !== null) {
        window.clearInterval(pingTimer);
        pingTimer = null;
      }
    };

    const scheduleReconnect = (): void => {
      if (disposed) return;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        connect();
      }, delay);
    };

    const connect = (): void => {
      if (disposed) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(socketUrl());
      } catch {
        setLastError('WebSocket konnte nicht geöffnet werden.');
        scheduleReconnect();
        return;
      }
      socket = ws;

      ws.onopen = () => {
        if (disposed) return;
        attempt = 0;
        setConnected(true);
        setLastError(null);
        pingTimer = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (message: MessageEvent<unknown>) => {
        if (disposed) return;
        const event = parseEvent(message.data);
        if (event === null) return;
        if (event.type === 'pong') return;
        handlerRef.current(event);
      };

      ws.onerror = () => {
        if (disposed) return;
        setLastError('Verbindungsfehler — neuer Versuch läuft.');
      };

      ws.onclose = (event: CloseEvent) => {
        if (disposed) return;
        setConnected(false);
        if (pingTimer !== null) {
          window.clearInterval(pingTimer);
          pingTimer = null;
        }
        if (event.code === 4401) {
          setLastError('Nicht angemeldet (Sitzung abgelaufen).');
          return;
        }
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposed = true;
      clearTimers();
      if (socket !== null) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
        socket = null;
      }
      setConnected(false);
    };
  }, [active]);

  return { connected, lastError };
}
